import { useRef, useEffect } from 'react';
import useChat from '../../hooks/chat/useChat';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ChatTypingIndicator } from './ChatTypingIndicator';

interface Props {
    projectTitle: string;
    onClose: () => void;
}

export const ChatWindow = ({ projectTitle, onClose }: Props) => {
    const { messages, isLoading, sendMessage, clearHistory, session, retryLastMessage } = useChat();
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Bloquear scroll del body en mobile cuando el chat esta abierto
    useEffect(() => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
            document.body.style.overflow = 'hidden';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (message: string) => {
        await sendMessage(message);
    };

    const lastMessage = messages[messages.length - 1];
    const showTyping = isLoading && (!lastMessage || lastMessage.role !== 'assistant' || !lastMessage.isStreaming);

    return (
        <div className="chat-panel tw-fixed tw-flex tw-flex-col tw-bg-white tw-overflow-hidden
                        tw-inset-0
                        md:tw-top-[54px] md:tw-bottom-0 md:tw-left-auto md:tw-right-0 md:tw-w-[420px] md:tw-shadow-[-4px_0_20px_rgba(0,0,0,0.1)] md:tw-border-l md:tw-border-gray-200"
            style={{ zIndex: 1100 }}>

            {/* Header */}
            <div className="tw-bg-primary tw-text-white tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between tw-shrink-0">
                <div className="tw-flex-1 tw-min-w-0 tw-mr-2">
                    <h3 className="tw-font-bold tw-text-base tw-m-0">
                        Asistente IRIS
                    </h3>
                    <p className="tw-text-xs tw-opacity-80 tw-truncate tw-m-0">
                        {projectTitle}
                    </p>
                </div>
                <div className="tw-flex tw-gap-1">
                    <button
                        onClick={clearHistory}
                        className="tw-p-1.5 hover:tw-bg-white/10 tw-rounded-full tw-transition-colors tw-border-0 tw-bg-transparent tw-text-white tw-cursor-pointer"
                        title="Limpiar conversacion"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="tw-w-4 tw-h-4">
                            <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.519.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 3.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
                        </svg>
                    </button>
                    <button
                        onClick={onClose}
                        className="tw-p-1.5 hover:tw-bg-white/10 tw-rounded-full tw-transition-colors tw-border-0 tw-bg-transparent tw-text-white tw-cursor-pointer"
                        title="Cerrar"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="tw-w-4 tw-h-4">
                            <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="chat-messages-scroll tw-flex-1 tw-overflow-y-auto tw-p-4 tw-space-y-3 tw-bg-gray-50">
                {messages.length === 0 && !isLoading && (
                    <div className="tw-text-center tw-text-gray-500 tw-py-6">
                        <div className="tw-w-12 tw-h-12 tw-mx-auto tw-mb-3 tw-rounded-full tw-bg-primary/10 tw-flex tw-items-center tw-justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="tw-w-6 tw-h-6 tw-text-primary">
                                <path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
                                <path d="M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 001.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0015.75 7.5z" />
                            </svg>
                        </div>
                        <p className="tw-mb-1 tw-font-medium tw-text-sm">Hola! Soy el asistente de IRIS</p>
                        <p className="tw-text-xs tw-mb-4">
                            Preguntame sobre precios, amenidades, financiamiento, comisiones y mas.
                        </p>
                        <div className="tw-flex tw-flex-col tw-gap-2 tw-px-4">
                            {[
                                'Haceme un speech de venta del proyecto',
                                'Que unidades hay disponibles y a que precio?',
                                'Que opciones de financiamiento hay?'
                            ].map((question, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleSend(question)}
                                    className="tw-text-left tw-text-xs tw-px-3 tw-py-2 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white hover:tw-bg-primary/5 hover:tw-border-primary/30 tw-text-gray-600 tw-transition-colors tw-cursor-pointer"
                                >
                                    {question}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((msg, idx) => (
                    <ChatMessage
                        key={idx}
                        message={msg}
                        countryCode={session?.countryCode}
                        projectTitle={projectTitle}
                        onRetry={msg.isTruncated && idx === messages.length - 1 ? retryLastMessage : undefined}
                    />
                ))}

                {showTyping && <ChatTypingIndicator />}

                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput onSend={handleSend} disabled={isLoading} />
        </div>
    );
};

export default ChatWindow;
